import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { apiGet, apiSend, fmtDate } from '../api';
import { theme, card, input, btnPrimary, btnSecondary } from '../theme';
// v0.23.0 — Name-the-report modal so new reports aren't all called "New report".
import NewReportModal from '../components/NewReportModal';
// v0.38.0 — Free-tier project cap. Hitting the cap shows UpgradePrompt.
import { isFreeTier, FREE_LIMITS } from '../utils/tier';
// 2026-08-17 — a failed site sync says what failed, not "Not signed in.".
import { syncFailureMessage } from '../utils/syncMessage';
import UpgradePrompt from '../components/UpgradePrompt';
// v0.46.7 — Shared page title (standardised heading size).
import PageHeading from '../components/PageHeading';
// v0.50.0 — lucide icons replace emoji glyphs in EmptyState.
import { Ruler, DownloadCloud, RefreshCw, AlertTriangle, CloudOff, Archive } from 'lucide-react';
// v0.55.0 — signed-in user for the diagnostic empty-state (which
// workspace am I in?) so an empty list never reads as "app broken".
import { getCurrentUser } from '../utils/user';
// v0.56.0 — client hand-off sessions read the tenant's Locations from the
// contact-cookie endpoint (they have no insite_sid), and can import their
// InSite sites. isClientContactSession() flips the whole screen read-only.
import { isClientContactSession, getContactSession } from '../utils/contact-session';
// v0.242.0 — Brett: "on the droppin locations list we need a toggle to display
// only locations with reports. Maybe a notification ... to show there is a new
// report." The three-state count rule and the new-report rule are pure and
// tested in src/utils/locationReports.test.mjs; this screen only draws them.
import { filterLocations, reportsCountOf, hasNewReport, countNewReports } from '../utils/locationReports';
import { lastSeenLookup, markSeen, markAllSeen, syncSeen } from '../utils/locationSeen';
import { FileText, BellDot } from 'lucide-react';
// v0.267.0 — the office's comments not emailed yet, per project (Send update).
import { UnsentUpdatesNotice } from '../components/UnsentUpdatesBar';
import { NewRepliesNotice } from '../components/NewReplies';
// v0.312.0 — counts with On hold and Cancelled (cancelled is out of the total).
import { statusCounts } from '../utils/defectStatus';

// Project list.
//
// v0.13.1 - grouped layout. Each project is now a CARD section with the
// project name as the header and its saved reports listed inside.
// Brett: "Projects still arent grouped... By project Name then report
// name." The flat one-row-per-project table never surfaced that DropPin
// supports many reports per project (v0.9.0+) - now the structure makes
// that visible at a glance: Project → Reports → defect counts per report.
//
// Loading model:
//   1. GET /api/projects                     fast, sets the cards
//   2. PARALLEL: per-project GET /api/defects + GET /api/reports
//      Each enrichment patches the project row independently so a slow
//      project doesn't block the others.
//
// v0.56.0 — CLIENT MODE. A client arriving via the InSpec client-portal
// hand-off holds ONLY a droppin_contact cookie (no insite_sid), so the
// admin data path above (/api/projects + per-project /api/defects and
// /api/reports) would 401. In client mode the screen instead loads a
// single read-only list from /api/contact-projects (Projects + inline
// defect counts, contact-cookie auth), hides every admin action (create /
// archive / new report), and offers an "Import my InSite sites" button so
// the client can pull their InSite locations into DropPin as Locations.
// v0.242.0 — the newest report's date in a list, or null. Used to decide
// whether a location is carrying something the reader has not seen.
// null on an empty list and on unparseable dates, NEVER 0: the epoch compares
// as older than every last-seen, so a 0 here would mark nothing new instead of
// something new — quiet, and wrong in the direction nobody notices.
function newestCreatedAt(reports) {
  const list = Array.isArray(reports) ? reports : [];
  let best = null;
  for (const r of list) {
    const raw = r && (r.createdAt || r.CreatedAt || r.updatedAt || r.UpdatedAt);
    const t = raw ? new Date(raw).getTime() : NaN;
    if (Number.isFinite(t) && (best == null || t > best)) best = t;
  }
  return best == null ? null : new Date(best).toISOString();
}

export default function ProjectsScreen() {
  const navigate = useNavigate();
  // v0.56.0 — read the flag ONCE per mount (a session change re-mounts via
  // AppShell). client === read-only, contact-cookie data path.
  const client = isClientContactSession();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  // v0.242.0 — "only locations with reports". Remembered, because a client who
  // only cares about the sites that have been reported on cares about that on
  // every visit, not once. A storage failure just means it starts off.
  const [onlyWithReports, setOnlyWithReports] = useState(() => {
    try { return localStorage.getItem('droppin_locs_with_reports_v1') === '1'; } catch (_) { return false; }
  });
  // Bumped when a location is opened, so the new-report markers redraw without
  // refetching. localStorage is not reactive; this is what makes it look it.
  const [seenTick, setSeenTick] = useState(0);
  const [creating, setCreating] = useState(false);
  // v0.23.0 — name-the-report modal target: { Id, Name } when open, null when closed.
  const [reportFor, setReportFor] = useState(null);
  // v0.38.0 — UpgradePrompt state for free-tier limit hits.
  const [upgradeReason, setUpgradeReason] = useState(null);
  // v0.56.0 — client "import my InSite sites" state.
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState(null);
  // v0.57.0 — auto-sync on first load. When the list is empty we pull the
  // signed-in user's InSite sites ONCE, non-blocking, before giving up to
  // the empty state. `syncing` drives a brief "Syncing your sites…" panel;
  // autoSyncedRef guarantees the auto-run fires at most once per mount so a
  // genuinely-empty account never loops. The manual button still works too.
  const [syncing, setSyncing] = useState(false);
  const autoSyncedRef = useRef(false);
  // HONEST STATES -- syncOutcome records the LAST InSite sync result so the
  // empty region never lies: null=not run yet, 'ok'=ran (0 new is fine),
  // 'pending'=InSite not connected yet, 'error'=sync failed. syncMsg is the
  // human-readable reason shown in the pending/error state + soft banner.
  const [syncOutcome, setSyncOutcome] = useState(null);
  const [syncMsg, setSyncMsg] = useState(null);
  // v0.90.0 (plan §4.3, Phase 3) — reconcile summary from the last sync:
  // archivedMsg = InSite-removed sites we auto-archived (neutral info);
  // blockedMsg  = removed sites KEPT because they carry defects (needs review).
  const [archivedMsg, setArchivedMsg] = useState(null);
  const [blockedMsg, setBlockedMsg] = useState(null);

  // v0.56.0 — client data path: one round-trip, read-only, contact-cookie.
  const loadClient = async () => {
    setLoading(true);
    try {
      const list = await apiGet(`/api/contact-projects${showArchived ? '?archived=1' : ''}`);
      const arr = Array.isArray(list) ? list : [];
      // Map the contact-projects shape -> the same row shape the card
      // render consumes (PascalCase Id/Name/Address/Status; reports is
      // empty so the card's reports block stays hidden; the *counts* are
      // already computed server-side).
      setProjects(arr.map(p => ({
        Id: p.id, Name: p.name, Address: p.address, Status: p.status,
        CustomerName: p.customerName,
        // v0.330.0 — a builder's site in this person's home ("h6-106"): the
        // builder's name. null on the client company's own sites.
        SharedBy: p.sharedBy || null,
        defectsTotal: p.defectsTotal, defectsOpen: p.defectsOpen,
        defectsDone: p.defectsDone,
        defectsRectified: null, defectsComplete: null,
        // v0.242.0 — the server counted the reports THIS CONTACT MAY SEE and
        // sent the newest one's date. `reports: []` below is the card's
        // per-report block, which a client card does not draw; it is NOT the
        // count, which is why reportsCountOf() prefers reportsTotal. null here
        // means the server could not count, and the filter leaves those alone.
        reportsTotal: (typeof p.reportsTotal === 'number') ? p.reportsTotal : null,
        latestReportAt: p.latestReportAt || null,
        reports: [], _defectsRaw: [],
      })));
      setError(null);
      return arr;
    } catch (e) {
      setError(e.message);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const list = await apiGet(`/api/projects${showArchived ? '?archived=1' : ''}`);
      const baseList = Array.isArray(list) ? list : [];
      // First paint: show every project immediately with null defect /
      // report stats so the user sees the layout before async fetches
      // complete.
      setProjects(baseList.map(p => ({
        ...p,
        defectsTotal: null, defectsOpen: null, defectsRectified: null,
        defectsComplete: null, defectsDone: null,
        reports: null,
        // v0.242.0 — UNKNOWN until the enrichment below lands. Not zero: the
        // "only with reports" filter must not blank the screen for the second
        // it takes, and must not hide a site for good if the fetch fails.
        reportsTotal: null, latestReportAt: null,
      })));
      setError(null);
      setLoading(false); // v0.63 fast-paint: show cards now; per-card counts patch in after (was gated behind ~2 calls/location)
      // v0.57.0 — hand the caller the count so it can decide whether to
      // auto-sync InSite sites (below). The enrichment still runs after.
      const __baseCount = baseList.length;

      // Parallel enrichment - defects + reports per project. Each project
      // is enriched independently so one slow request doesn't gate the
      // others.
      const enriched = await Promise.all(baseList.map(async (p) => {
        const id = p.Id ?? p.id;
        if (!id) return { ...p, defectsTotal: 0, defectsOpen: 0, defectsRectified: 0, reports: [], reportsTotal: 0, latestReportAt: null };
        const [defects, reports] = await Promise.all([
          apiGet(`/api/defects?projectId=${id}`).catch(() => []),
          // v0.242.0 — null, NOT []. A fetch that failed has not told us this
          // location has no reports; it has told us nothing. The old `[]` made
          // the two indistinguishable, which was harmless while the array only
          // fed a display list and is not harmless now that a filter acts on
          // it: a location would vanish from a filtered list because one
          // request fell over.
          apiGet(`/api/reports?projectId=${id}`).catch(() => null),
        ]);
        const defArr = Array.isArray(defects) ? defects : [];
        const repArr = Array.isArray(reports) ? reports : null;
        // v0.14.0 - 3-state counts. "Done" = rectified + complete (any
        // defect that's had work done, whether or not the owner has
        // signed it off yet). "Open" = anything not done.
        // v0.312.0 — a cancelled item is out of the total and the % done;
        // an on-hold one is still outstanding.
        const c = statusCounts(defArr);
        return {
          ...p,
          defectsTotal: c.total,
          defectsOpen: c.total - c.done,
          defectsOnHold: c.on_hold,
          defectsCancelled: c.cancelled,
          defectsRectified: c.rectified,
          defectsComplete: c.complete,
          defectsDone: c.done,
          // The card's per-report block still wants a list it can map over.
          reports: repArr || [],
          // v0.242.0 — an ANSWER now, where the first paint had null; and
          // still null when the fetch failed, which is the honest answer.
          reportsTotal: repArr ? repArr.length : null,
          latestReportAt: repArr ? newestCreatedAt(repArr) : null,
          // Keep the full defect list so per-report counts can be
          // derived locally from each report's settingsJson without
          // a second API roundtrip.
          _defectsRaw: defArr,
        };
      }));
      setProjects(enriched);
      return __baseCount;
    } catch (e) {
      setError(e.message);
      return 0;
    } finally {
      setLoading(false);
    }
  };

  // Pull the signed-in user's InSite sites via /api/import-locations and record
  // an HONEST outcome so the empty region never lies:
  //   'ok'      -- sync completed (0 imported just means nothing new)
  //   'pending' -- InSite has not exposed this company's locations yet
  //   'error'   -- sync failed (network / server); saved locations untouched
  // Returns { outcome, imported } so the caller can reload when rows landed.
  // v0.91.0 — pass { force:true } for a USER-INITIATED sync (Import button /
  // empty-state Retry): it bypasses the server's 10-min auto re-sync guard.
  // The mount auto-sync stays unforced so repeated reloads don't re-hit InSpec
  // (a throttled response is a quiet success no-op: imported 0, outcome 'ok').
  const doSync = async (opts = {}) => {
    setSyncing(true); setSyncMsg(null);
    setArchivedMsg(null); setBlockedMsg(null);
    let r = null, outcome = 'ok';
    try {
      r = await apiSend('POST', '/api/import-locations', opts.force ? { force: true } : {});
      if (r && (r.offline || r.queued)) {
        outcome = 'pending';
        setSyncMsg("You appear to be offline. We'll sync your InSite sites when you're back online.");
        setImportMsg(null);
      } else if (r && r.pending) {
        outcome = 'pending';
        setSyncMsg(r.message || "InSite hasn't exposed your locations yet.");
        setImportMsg(null);
      } else {
        const imported = (r && r.imported) || 0;
        setImportMsg(imported > 0
          ? ('Added ' + imported + ' new location' + (imported === 1 ? '' : 's') + ' from InSite.')
          : null);
        // v0.90.0 — surface the delete-reconciliation outcome honestly.
        const archived = (r && r.archived) || 0;
        const blocked = (r && r.archiveBlocked) || 0;
        setArchivedMsg(archived > 0
          ? (archived + ' InSite site' + (archived === 1 ? '' : 's') + ' removed — archived')
          : null);
        setBlockedMsg(blocked > 0
          ? (blocked + ' removed site' + (blocked === 1 ? '' : 's') + ' kept — they have defects; review before archiving')
          : null);
      }
    } catch (e) {
      outcome = 'error';
      // 2026-08-17 — was `e.message`, which printed the API's own words. A 401
      // from /api/import-locations says "Not signed in.", so a failed sync
      // announced that the user was signed out while they sat looking at their
      // locations. See utils/syncMessage.js.
      setSyncMsg(syncFailureMessage(e));
      setImportMsg(null);
    }
    setSyncOutcome(outcome);
    setSyncing(false);
    return { outcome, imported: (r && r.imported) || 0 };
  };

  // Manual "Import my InSite sites" button: run the sync, then reload the list.
  const runImport = async () => {
    setImporting(true);
    const { outcome } = await doSync({ force: true });
    if (outcome === 'ok') { try { sessionStorage.setItem('droppin_loc_synced_v1', '1'); } catch (_) {} }
    await (client ? loadClient : load)();
    setImporting(false);
  };

  // Retry used by the honest empty-region states: reload, then sync if empty.
  const retryAll = async () => {
    const runLoad = client ? loadClient : load;
    const list = await runLoad();
    const count = Array.isArray(list) ? list.length : Number(list || 0);
    if (count === 0) {
      const { outcome, imported } = await doSync({ force: true });
      if (outcome === 'ok') { try { sessionStorage.setItem('droppin_loc_synced_v1', '1'); } catch (_) {} }
      if (imported > 0) { await runLoad(); }
    }
  };

  // Mount / showArchived-change loader with a ONE-SHOT auto-sync. Load the list,
  // then (at most once per mount) sync the signed-in user's InSite sites so they
  // appear with no button click. The once-per-session flag (v0.63 background
  // backfill) is now set ONLY after a SUCCESSFUL sync, so a failed/pending sync
  // retries on the next visit instead of being suppressed for the whole session.
  // Idempotent (import-locations MERGEs); any failure surfaces an honest state.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const runLoad = client ? loadClient : load;
      const list = await runLoad();
      if (cancelled) return;
      const count = Array.isArray(list) ? list.length : Number(list || 0);
      if (autoSyncedRef.current) return; // auto-sync at most once per mount
      let alreadySynced = false;
      try { alreadySynced = sessionStorage.getItem('droppin_loc_synced_v1') === '1'; } catch (_) {}
      if (count === 0 || !alreadySynced) {
        autoSyncedRef.current = true;
        const { outcome, imported } = await doSync();
        if (cancelled) return;
        if (outcome === 'ok') { try { sessionStorage.setItem('droppin_loc_synced_v1', '1'); } catch (_) {} }
        if (imported > 0) { await runLoad(); }
      }
    })();
    return () => { cancelled = true; };
  }, [showArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  // v0.242.0 — search and the "only with reports" toggle, decided in one pure
  // place (utils/locationReports.js) so the three-state count rule is the same
  // for a client session and an admin one, and so it is testable at all.
  // `unknown` is how many of the rows on screen are still being counted; it is
  // said out loud below rather than quietly filtered away.
  const { rows: filtered, unknown: stillCounting, hiddenNoReports } =
    filterLocations(projects, { q, onlyWithReports });

  // v0.242.0 — the new-report markers. Read once per render pass; seenTick
  // forces a fresh read after a location is opened.
  const seenLookup = React.useMemo(() => lastSeenLookup(), [seenTick]); // eslint-disable-line react-hooks/exhaustive-deps
  const newCount = countNewReports(projects, seenLookup);
  // v0.338.0 — the marks are kept per person on the server, so a site read on
  // the phone is not "new" on the laptop. Merge once per mount, then redraw.
  const seenAs = client ? 'contact' : 'office';
  useEffect(() => {
    let cancelled = false;
    syncSeen(seenAs).then(() => { if (!cancelled) setSeenTick(t => t + 1); });
    return () => { cancelled = true; };
  }, [seenAs]);

  const openLocation = (p, to) => {
    // Marked on the CLICK, not on render: a marker cleared by the list drawing
    // itself is a marker nobody ever sees.
    markSeen(p.Id, undefined, seenAs);
    setSeenTick(t => t + 1);
    navigate(to);
  };

  const toggleOnlyWithReports = (on) => {
    setOnlyWithReports(on);
    try { localStorage.setItem('droppin_locs_with_reports_v1', on ? '1' : '0'); } catch (_) {}
  };

  const clientCompany = client ? (getContactSession()?.contact?.company || null) : null;

  return (
    <div>
      <div className="ss-toolbar" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <PageHeading>Locations</PageHeading>
          <div style={{ fontSize: 13, color: theme.textLight, marginTop: 4 }}>
            {client
              ? 'Your sites. Each location can hold reports and defects.'
              : 'A location is a site / store / building. Each location can hold many reports.'}
          </div>
        </div>
        <div className="ss-toolbar-actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            placeholder="Search locations…"
            value={q}
            onChange={e => setQ(e.target.value)}
            style={{ ...input, width: 240, maxWidth: '100%' }}
          />
          {/* v0.242.0 — Brett: "a toggle to display only locations with
              reports". Shown to BOTH sessions: a builder with sixty sites
              wants it as much as a client with six, and the rule underneath
              is identical. The count beside it is what the toggle would
              remove, so the effect is legible before it is used. */}
          <label
            title="Hide locations that have no reports. Locations still being counted stay on the list."
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: theme.textLight, cursor: 'pointer' }}
          >
            <input
              type="checkbox"
              checked={onlyWithReports}
              onChange={e => toggleOnlyWithReports(e.target.checked)}
            />
            <FileText size={14} aria-hidden="true" />
            Only with reports
          </label>
          {/* v0.56.0 — admin-only controls (archive toggle + create) are
              hidden for a read-only client session. The client instead
              gets an "Import my InSite sites" action. */}
          {!client && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: theme.textLight, cursor: 'pointer' }}>
              <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
              Show archived
            </label>
          )}
          {!client && (
            /* v0.38.0 — gate "+ New Project" behind the free-tier active-project cap. */
            <button
              onClick={() => {
                if (isFreeTier()) {
                  const active = projects.filter(p => (p.Status || p.status || 'active') !== 'archived').length;
                  if (active >= FREE_LIMITS.activeProjects) {
                    setUpgradeReason('activeProjects');
                    return;
                  }
                }
                setCreating(true);
              }}
              style={btnPrimary}
            >+ New location</button>
          )}
        </div>
      </div>

      {/* v0.242.0 — THE TOGGLE SAYS WHAT IT DID, and what it has not been able
          to do yet. A filtered list that is shorter than expected, with no
          explanation, is the failure mode this whole feature invites: the
          reader cannot tell "no reports here" from "not counted yet". */}
      {onlyWithReports && (hiddenNoReports > 0 || stillCounting > 0) && (
        <div style={{ padding: '8px 12px', background: theme.bg, border: `1px solid ${theme.border}`, color: theme.textLight, borderRadius: 6, marginBottom: 12, fontSize: 12.5 }}>
          {hiddenNoReports > 0 && (
            <span>
              {hiddenNoReports} location{hiddenNoReports === 1 ? '' : 's'} with no reports hidden.
            </span>
          )}
          {stillCounting > 0 && (
            <span style={{ marginLeft: hiddenNoReports > 0 ? 8 : 0 }}>
              Still counting {stillCounting} location{stillCounting === 1 ? '' : 's'} — they stay on the list until we know.
            </span>
          )}
        </div>
      )}

      {/* v0.242.0 — Brett: "a notification ... to show there is a new report."
          A line, not a modal: it names the number and it clears by being used.
          It only appears when a location is actually carrying a report newer
          than the last time this browser opened it. */}
      {newCount > 0 && (
        <div style={{ padding: '10px 12px', background: theme.bg, border: `1px solid ${theme.brand}`, color: theme.text, borderRadius: 6, marginBottom: 12, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <BellDot size={16} aria-hidden="true" style={{ color: theme.brand, flexShrink: 0 }} />
          <span style={{ flex: '1 1 auto' }}>
            <strong>{newCount} location{newCount === 1 ? ' has' : 's have'} a new report</strong>
            {' '}since you last opened {newCount === 1 ? 'it' : 'them'}.
          </span>
          <button
            type="button"
            onClick={() => { markAllSeen(projects, undefined, seenAs); setSeenTick(t => t + 1); }}
            style={{ ...btnSecondary, padding: '4px 10px', fontSize: 12 }}
          >Mark all as seen</button>
        </div>
      )}

      {/* v0.267.0 — Brett: "Maybe a dashboard item to say we have unsent."
          Staff only: a client's own replies are sent as they type them. */}
      {!client && <UnsentUpdatesNotice onOpen={(id) => navigate(`/projects/${id}`)} />}
      {/* v0.269.0 — replies this person has not read; each opens the
          report holding the newest one, not just the project's latest. */}
      {!client && (
        <NewRepliesNotice
          onOpen={(id, rid) => navigate(rid
            ? `/projects/${id}/register?reportId=${encodeURIComponent(rid)}`
            : `/projects/${id}`)}
        />
      )}

      {/* Success banner -- how many locations the last import added. */}
      {importMsg && (
        <div style={{ padding: 12, background: theme.bg, border: `1px solid ${theme.border}`, color: theme.brand, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
          {importMsg}
        </div>
      )}

      {/* v0.90.0 (Phase 3) — delete-reconciliation summary, in the SAME
          soft-notice primitive as the sync notice below. Event notices (like
          the import banner) so they show whenever the last sync archived or
          blocked something, independent of the current list length. */}
      {archivedMsg && (
        <div style={{ padding: 12, background: theme.bg, border: `1px solid ${theme.border}`, color: theme.textLight, borderRadius: 6, marginBottom: 12, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Archive size={15} />
          <span>{archivedMsg}</span>
        </div>
      )}
      {blockedMsg && (
        <div style={{ padding: 12, background: theme.bg, border: `1px solid ${theme.border}`, color: theme.warn, borderRadius: 6, marginBottom: 12, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={15} />
          <span>{blockedMsg}</span>
        </div>
      )}

      {/* Soft, non-blocking notice when a BACKGROUND re-sync could not complete
          but we still have locations to show. The honest full states below cover
          the empty case, so this only appears alongside an existing list. */}
      {syncMsg && projects.length > 0 && (
        <div style={{ padding: 12, background: theme.bg, border: `1px solid ${theme.border}`, color: theme.textLight, borderRadius: 6, marginBottom: 12, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
          {syncOutcome === 'error' ? <AlertTriangle size={15} /> : <CloudOff size={15} />}
          <span>{syncMsg}</span>
        </div>
      )}

      {error && projects.length > 0 && (
        <div style={{ padding: 12, background: theme.bg, border: `1px solid ${theme.danger}`, color: theme.danger, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ ...card, textAlign: 'center', color: theme.textLight, padding: 40 }}>Loading…</div>
      ) : (filtered.length === 0 && (syncing || (syncOutcome === null && !error))) ? (
        /* Sync in flight OR not run yet -- never flash a false "No locations yet". */
        <div style={{ ...card, textAlign: 'center', color: theme.textLight, padding: 40 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <DownloadCloud size={18} />
            <span>Checking for your InSite sites…</span>
          </div>
          <div style={{ fontSize: 12, marginTop: 6 }}>Bringing your InSite sites into DropPin.</div>
        </div>
      ) : filtered.length === 0 ? (
        error ? (
          <SyncStateCard
            tone="error"
            icon={<AlertTriangle size={44} />}
            title="Couldn't load your locations"
            body={error}
            actionLabel="Try again"
            onAction={retryAll}
          />
        ) : syncOutcome === 'error' ? (
          <SyncStateCard
            tone="error"
            icon={<AlertTriangle size={44} />}
            title="Couldn't sync your InSite sites"
            body={syncMsg || 'We hit a problem reaching InSite. Your saved locations are unaffected.'}
            actionLabel="Try again"
            onAction={retryAll}
          />
        ) : syncOutcome === 'pending' ? (
          <SyncStateCard
            icon={<CloudOff size={44} />}
            title="Your InSite sites aren't connected yet"
            body={syncMsg || "InSite hasn't exposed your locations yet. Nothing is missing on your end."}
            actionLabel="Check again"
            onAction={retryAll}
          />
        ) : client ? (
          <ClientEmptyState hasFilter={!!q} company={clientCompany} onImport={runImport} importing={importing} />
        ) : (
          <EmptyState onCreate={() => setCreating(true)} hasFilter={!!q} user={getCurrentUser()} />
        )
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {filtered.map(p => (
            client ? (
              <ClientLocationCard
                key={p.Id}
                p={p}
                isNew={hasNewReport(p, seenLookup(p))}
                onOpen={() => openLocation(p, `/projects/${p.Id}/site-reports`)}
              />
            ) : (
              <ProjectCard
                key={p.Id}
                p={p}
                isNew={hasNewReport(p, seenLookup(p))}
                onOpenProject={() => openLocation(p, `/projects/${p.Id}`)}
                onOpenReport={(reportId) => navigate(`/projects/${p.Id}/register?reportId=${reportId}`)}
                onNewReport={() => {
                  // v0.23.0 — Brett: "you havent given me the opportunity to
                  // name the report" + "why cant i go straight to the new
                  // report page". Open the naming modal here; the modal's
                  // onCreate handler (below) does the POST and lands the
                  // user directly on the report-scoped detail view.
                  setReportFor({ Id: p.Id, Name: p.Name || p.name });
                }}
              />
            )
          ))}
        </div>
      )}

      {creating && (
        <CreateProjectModal
          onClose={() => setCreating(false)}
          onSaved={(p) => { setCreating(false); navigate(`/projects/${p.Id}`); }}
        />
      )}

      {/* v0.23.0 — naming modal for new reports from the project register.
          The user names it, we POST, then navigate straight into the
          report-scoped detail view (skipping the project overview that
          Brett complained looked like an unnecessary detour). */}
      {reportFor && (
        <NewReportModal
          projectName={reportFor.Name}
          projectId={reportFor.Id}
          onCancel={() => setReportFor(null)}
          onCreate={async ({ name, description, seedMembers }) => {
            const initialSettings = { explicitDefectIds: [] };
            const created = await apiSend('POST', '/api/reports', {
              projectId: reportFor.Id,
              name,
              description,
              settingsJson: initialSettings,
              // v0.45.10 — Forward seedMembers from the modal (owner +
              // optional project team) so the backend clones them into
              // ReportTeamMembers.
              seedMembers,
            });
            const newId = created.id ?? created.Id;
            setProjects(prev => prev.map(row =>
              row.Id === reportFor.Id
                ? { ...row, reports: [{ id: newId, name, description, settingsJson: initialSettings, CreatedAt: new Date().toISOString() }, ...(row.reports || [])] }
                : row
            ));
            // Direct land on the report-scoped view — ?reportId in
            // the URL switches ProjectDetailScreen into report-home
            // mode, which (also new in v0.23.0) hides the reports
            // list and shows only the working chrome for THIS report.
            setReportFor(null);
            navigate(`/projects/${reportFor.Id}?reportId=${newId}`);
          }}
        />
      )}

      {/* v0.38.0 — Free-tier upgrade nudge. */}
      <UpgradePrompt
        open={!!upgradeReason}
        reason={upgradeReason}
        onClose={() => setUpgradeReason(null)}
      />
    </div>
  );
}

// Honest empty-region state card. Mirrors the ImportFromInSiteModal
// pending/error pattern (theme tokens + RefreshCw retry) so a failed or
// not-yet-connected sync reads as a clear, RECOVERABLE state -- never a
// false "No locations yet". tone='error' tints the icon with the danger token.
function SyncStateCard({ icon, title, body, actionLabel, onAction, tone }) {
  const accent = tone === 'error' ? theme.danger : theme.textLight;
  return (
    <div style={{ ...card, textAlign: 'center', padding: 48 }}>
      <div style={{ display: 'flex', justifyContent: 'center', color: accent, opacity: tone === 'error' ? 0.9 : 0.5, marginBottom: 8 }}>
        {icon}
      </div>
      <div style={{ fontSize: 16, fontWeight: 600 }}>{title}</div>
      {body && (
        <div style={{ fontSize: 13, color: theme.textLight, marginTop: 6, maxWidth: 420, marginLeft: 'auto', marginRight: 'auto' }}>
          {body}
        </div>
      )}
      {onAction && (
        <button
          onClick={onAction}
          style={{ ...btnSecondary, marginTop: 16, display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <RefreshCw size={14} /> {actionLabel || 'Try again'}
        </button>
      )}
    </div>
  );
}

// v0.242.0 — "there is a new report here". One badge, both cards, so the
// client's Locations list and the builder's cannot end up saying it
// differently. It is a marker and not a message: it carries no report name
// and no count of reports, only "since you last looked, something landed".
function NewReportBadge() {
  return (
    <span
      title="A report here is newer than the last time you opened this location"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        background: theme.brand, color: '#fff',
        fontSize: 10.5, fontWeight: 800, letterSpacing: 0.3,
        padding: '2px 7px', borderRadius: 999, whiteSpace: 'nowrap',
      }}
    >
      <BellDot size={11} aria-hidden="true" />
      NEW REPORT
    </span>
  );
}

// v0.242.0 — the report count, which is the fact the "only with reports"
// toggle acts on, shown so the toggle is never mysterious. UNKNOWN SAYS SO
// rather than drawing "0 reports" over a number we do not have — the same
// distinction the filter itself makes.
function ReportStat({ p }) {
  const n = reportsCountOf(p);
  if (n == null) {
    return <span style={{ fontSize: 11.5, color: theme.textLight, opacity: 0.7 }}>counting…</span>;
  }
  return (
    <span
      title={n === 0 ? 'No reports at this location' : n + ' report' + (n === 1 ? '' : 's')}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: 11.5, fontWeight: 700,
        color: n > 0 ? theme.text : theme.textLight,
        opacity: n > 0 ? 1 : 0.75,
      }}
    >
      <FileText size={12} aria-hidden="true" />
      {n}
    </span>
  );
}

// v0.56.0 — read-only Location card for a CLIENT hand-off session.
// v0.x — now CLICKABLE. Clicking opens the site's read-only reports view
// (/projects/:id/site-reports), a client-safe screen backed by
// /api/contact-reports (contact-cookie auth). Before this the card had no
// navigation because the client has no insite_sid, so /projects/:id
// (ProjectDetailScreen) 401s — the reason clicking a location did nothing.
function ClientLocationCard({ p, isNew, onOpen }) {
  const open = () => { if (onOpen) onOpen(); };
  return (
    <div
      style={{
        ...card,
        padding: 0,
        overflow: 'hidden',
        borderLeft: `4px solid ${theme.brand}`,
        background: 'transparent',
      }}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={open}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
        onMouseEnter={(e) => { e.currentTarget.style.background = theme.bg; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = theme.surface; }}
        title="View this site's reports"
        style={{
          background: theme.surface,
          padding: '14px 18px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 14, flexWrap: 'wrap',
          cursor: 'pointer',
          transition: 'background-color 0.12s',
        }}
      >
        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h2 style={{ fontSize: 17, fontWeight: 800, margin: 0, color: theme.text }}>{p.Name}</h2>
            {isNew && <NewReportBadge />}
            {String(p.Status || '').toLowerCase() !== 'active' && <StatusPill status={p.Status} />}
          </div>
          {p.Address && (
            <div style={{ fontSize: 12, color: theme.textLight, marginTop: 2 }}>{p.Address}</div>
          )}
          {p.SharedBy && (
            <div data-shared-by-site={p.Id} style={{ fontSize: 12, fontWeight: 700, color: theme.text, marginTop: 2 }}>
              Shared with you by {p.SharedBy}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <ReportStat p={p} />
          <DefectStat p={p} />
          <span aria-hidden="true" style={{ fontSize: 18, color: theme.brand, opacity: 0.7 }}>&rarr;</span>
        </div>
      </div>
    </div>
  );
}

// v0.56.0 — client empty-state. Distinct from the admin EmptyState: a
// client can't "create" a location, so the call to action is to IMPORT
// their InSite sites (or ask InSite to add them).
function ClientEmptyState({ hasFilter, company, onImport, importing }) {
  return (
    <div style={{ ...card, textAlign: 'center', padding: 60 }}>
      <div style={{ opacity: 0.3, marginBottom: 0 }}><Ruler size={48} /></div>
      <div style={{ fontSize: 16, fontWeight: 600, marginTop: 8 }}>
        {hasFilter ? 'No locations match' : 'No locations yet'}
      </div>
      <div style={{ fontSize: 13, color: theme.textLight, marginTop: 4, maxWidth: 400, marginLeft: 'auto', marginRight: 'auto' }}>
        {hasFilter
          ? 'Try a different search, or clear the filter.'
          : (company
              ? `Bring ${company}'s InSite sites into DropPin to see them here.`
              : 'Bring your InSite sites into DropPin to see them here.')}
      </div>
    </div>
  );
}
// v0.19.1 — Project card refined again.
// Changes vs v0.19.0:
//   * Open/total/rectified stats block in the header DELETED. Brett
//     X-ed it out — the rolled-up counts confused him (he interpreted
//     them as report data) and they duplicated info now shown on each
//     report row.
//   * Small "+ New report" icon button added to the header right
//     (next to the chevron). Click POSTs a default report, the card
//     updates inline with the new row, and the user navigates to
//     that report's defect register. No intermediate Reports manager.
//   * Each report row now carries pills — total + open defect counts
//     computed by re-running the report's settingsJson filters
//     against the project's defects (helper restored at the bottom
//     of this file, deleted in v0.18.0).
function ProjectCard({ p, isNew, onOpenProject, onOpenReport, onNewReport }) {
  const reports = Array.isArray(p.reports) ? p.reports : [];
  const defects = Array.isArray(p._defectsRaw) ? p._defectsRaw : [];
  return (
    <div
      style={{
        ...card,
        padding: 0,
        overflow: 'hidden',
        borderLeft: `4px solid ${theme.brand}`,
        background: 'transparent',
      }}
    >
      {/* Header band — click the project info area opens the project.
          The "+ new report" icon on the right has its own click handler
          (stops propagation). */}
      <div
        style={{
          background: theme.surface,
          padding: '14px 18px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 14, flexWrap: 'wrap',
          transition: 'background-color 0.12s',
        }}
      >
        <div
          onClick={onOpenProject}
          onMouseEnter={(e) => e.currentTarget.style.color = theme.brand}
          onMouseLeave={(e) => e.currentTarget.style.color = theme.text}
          style={{
            flex: '1 1 240px', minWidth: 0,
            cursor: 'pointer',
            color: theme.text,
            transition: 'color 0.12s',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h2 style={{ fontSize: 17, fontWeight: 800, margin: 0, color: 'inherit' }}>{p.Name}</h2>
            {isNew && <NewReportBadge />}
            <StatusPill status={p.Status} />
          </div>
          {p.Address && (
            <div style={{ fontSize: 12, color: theme.textLight, marginTop: 2 }}>{p.Address}</div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* v0.19.1 — Compact "+ New report" icon. Stops propagation
              so clicking it doesn't also trigger the project open. */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onNewReport && onNewReport(); }}
            title="Create a new report for this project"
            aria-label="New report"
            style={{
              width: 32, height: 32, borderRadius: 999,
              border: `1px solid ${theme.brand}`,
              background: theme.surface, color: theme.brand,
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, fontWeight: 700,
              padding: 0, lineHeight: 1,
            }}
          >+</button>
          <span
            onClick={onOpenProject}
            style={{ fontSize: 18, color: theme.brand, opacity: 0.7, cursor: 'pointer' }}
          >&rarr;</span>
        </div>
      </div>

      {/* v0.19.0 — Inline reports list. Quiet on empty / loading.
          v0.45.10 — Archived reports (settingsJson.archived === true)
          are filtered out of the default view. */}
      {(() => {
        const visibleReports = (reports || []).filter(r => {
          try {
            const s = (typeof r.settingsJson === 'string') ? JSON.parse(r.settingsJson || '{}') : (r.settingsJson || {});
            return !s || !s.archived;
          } catch { return true; }
        });
        if (visibleReports.length === 0) return null;
        return (
          <div style={{ padding: '4px 14px 10px', borderTop: '1px dashed var(--border, #e5e7eb)' }}>
            {visibleReports.map(r => (
              <ReportRow
                key={r.id}
                r={r}
                allDefects={defects}
                onClick={(e) => { e.stopPropagation(); onOpenReport && onOpenReport(r.id); }}
              />
            ))}
          </div>
        );
      })()}
    </div>
  );
}

// v0.19.1 — Report row with pills. Restored from v0.17.x with simpler
// styling: name + description on the left, defect-count pills on the
// right, chevron at the far right.
//
// v0.45.10 — Filtering rule:
//   1. If the report's settingsJson.explicitDefectIds is a non-empty
//      array, use that as the curated allow-list (overrides everything).
//   2. Otherwise, filter the project's defects to those whose
//      reportId == this report's id. That's the natural "this defect
//      belongs to this report" relationship (Defects.ReportId column).
//      Brett caught the previous behaviour where every report showed
//      the same totals — that was v0.45.6's "empty allow-list = show
//      all project defects" change, now reverted.
function ReportRow({ r, allDefects, onClick }) {
  const settings = (() => {
    try {
      if (!r.settingsJson) return {};
      if (typeof r.settingsJson === 'string') return JSON.parse(r.settingsJson);
      return r.settingsJson || {};
    } catch { return {}; }
  })();
  let filtered;
  if (Array.isArray(settings.explicitDefectIds) && settings.explicitDefectIds.length > 0) {
    const ids = new Set(settings.explicitDefectIds.map(Number));
    filtered = allDefects.filter(d => ids.has(Number(d.id)));
  } else {
    filtered = allDefects.filter(d => Number(d.reportId) === Number(r.id));
  }
  // v0.312.0 — cancelled items are out of the total.
  const { total, done } = statusCounts(filtered);
  const open = total - done;

  return (
    <div
      onClick={onClick}
      onMouseEnter={(e) => e.currentTarget.style.background = theme.bg}
      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12,
        padding: '6px 10px',
        cursor: 'pointer',
        borderRadius: 4,
        transition: 'background-color 0.12s',
      }}
      title="Open defect register filtered to this report"
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
        <span style={{ fontSize: 12, color: theme.textLight }}>📄</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {r.name || 'Untitled report'}
        </span>
        {r.description && (
          <span style={{ fontSize: 11, color: theme.textLight, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            · {r.description}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        {total > 0 ? (
          <>
            <Pill bg={theme.bg} fg={theme.warn}>{open} open</Pill>
            <Pill bg={theme.bg} fg={theme.textLight}>{total} total</Pill>
            {done === total && total > 0 && <Pill bg={theme.bg} fg={theme.success}>complete</Pill>}
          </>
        ) : (
          <Pill bg={theme.bg} fg={theme.textLight}>empty</Pill>
        )}
        <span style={{ fontSize: 13, color: theme.brand, opacity: 0.7, marginLeft: 4 }}>&rarr;</span>
      </div>
    </div>
  );
}

// Small chip helper used by report-row pills + (future) other rollups.
function Pill({ bg, fg, children }) {
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700,
      background: bg, color: fg, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

// v0.19.1 — Restored from v0.17.x. Applies the saved report's
// settingsJson filter spec to the project's full defect list so
// per-report counts can be computed client-side without an extra
// round trip. Mirrors the same filter logic used by
// defect-report-template.js on the server, so the pill numbers
// here match the PDF the user would generate.
// v0.21.0 — Added explicitDefectIds support. New reports own a list
// of defect IDs that belong to them; the report shows ONLY those
// defects, regardless of filter chips. An empty array means the
// report is intentionally empty (just-created). Backwards
// compatible: reports saved before v0.21.0 don't have
// explicitDefectIds and continue to use the filter spec.
// Export keyword so RegisterScreen + other consumers can share it.
export function applyReportFilters(defects, settingsRaw) {
  let s = null;
  try {
    if (!settingsRaw) return defects;
    s = (typeof settingsRaw === 'string') ? JSON.parse(settingsRaw) : settingsRaw;
  } catch { return defects; }
  if (!s || typeof s !== 'object') return defects;
  // v0.21.0 — Explicit allow-list takes priority WHEN IT HAS ENTRIES.
  // v0.45.10 — Empty array semantics: this helper used to be called
  // from ProjectsScreen.ReportRow with ALL the project's defects as
  // input — making empty-array-means-show-all return every defect
  // for every report (Brett's "each report has the same totals?"
  // bug). ReportRow now does its own reportId-based filter and
  // doesn't pass through here. The only remaining consumer is
  // RegisterScreen, which feeds defects ALREADY pre-fetched with
  // ?reportId=N — so they're already scoped to the report's
  // direct Defects.ReportId match. An empty explicitDefectIds here
  // means "no further curation, show all report-scoped defects" —
  // not "show zero". Non-empty means "narrow to this curated subset".
  if (Array.isArray(s.explicitDefectIds) && s.explicitDefectIds.length > 0) {
    const ids = new Set(s.explicitDefectIds.map(Number));
    return defects.filter(d => ids.has(Number(d.id)));
  }
  const f = s.filters || {};
  let out = defects;
  if (Array.isArray(f.status)   && f.status.length)   out = out.filter(d => f.status.includes(String(d.status || 'open')));
  if (Array.isArray(f.priority) && f.priority.length) out = out.filter(d => f.priority.includes(String(d.priority || 'medium')));
  if (Array.isArray(f.trade)    && f.trade.length)    out = out.filter(d => f.trade.includes(String(d.trade || 'general')));
  if (Array.isArray(f.drawing)  && f.drawing.length) {
    const ids = f.drawing.map(Number);
    out = out.filter(d => d.drawingId != null && ids.includes(Number(d.drawingId)));
  }
  return out;
}

function StatusPill({ status }) {
  const map = {
    active:   { label: 'Active',   bg: theme.bg, fg: theme.success },
    archived: { label: 'Archived', bg: theme.bg, fg: theme.textLight },
    complete: { label: 'Complete', bg: theme.bg, fg: theme.brand },
  };
  const m = map[status] || { label: status, bg: theme.bg, fg: theme.textLight };
  return (
    <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 600, background: m.bg, color: m.fg }}>
      {m.label}
    </span>
  );
}

// v0.12.0 - small stat for the card header. Same logic as before, just
// formatted compactly to fit beside the project name.
function DefectStat({ p }) {
  if (p.defectsTotal == null) {
    return <span style={{ color: theme.textLight, fontSize: 12 }}>…</span>;
  }
  if (p.defectsTotal === 0) {
    return <span style={{ color: theme.textLight, fontSize: 12 }}>no defects yet</span>;
  }
  // v0.14.0 - 3-state. Progress bar = (rectified + complete) / total.
  // Fallback to defectsRectified for old data shapes that didn't carry
  // defectsDone.
  const done = p.defectsDone ?? ((p.defectsRectified || 0) + (p.defectsComplete || 0));
  const pct = Math.round((done / p.defectsTotal) * 100);
  return (
    <div style={{ minWidth: 150, textAlign: 'right' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end', gap: 6 }}>
        <span style={{ fontWeight: 700, fontFamily: 'monospace', color: theme.text, fontSize: 14 }}>{p.defectsOpen}</span>
        <span style={{ fontSize: 11, color: theme.textLight }}>open</span>
        <span style={{ fontSize: 11, color: theme.textLight }}>/</span>
        <span style={{ fontWeight: 600, fontFamily: 'monospace', color: theme.textLight, fontSize: 13 }}>{p.defectsTotal}</span>
        <span style={{ fontSize: 11, color: theme.textLight }}>total</span>
      </div>
      <div style={{
        marginTop: 4, height: 5, background: theme.border,
        borderRadius: 3, overflow: 'hidden', width: '100%', maxWidth: 160, marginLeft: 'auto',
      }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: pct === 100 ? theme.success : theme.brand,
        }} />
      </div>
      <div style={{ fontSize: 10, color: theme.textLight, marginTop: 2 }}>
        {/* v0.336.0 - a client's site carries only "done" (the split is the
            builder's own working), so it never reads "0 rectified · 0 complete". */}
        {p.defectsOnHold > 0 && `${p.defectsOnHold} on hold · `}
        {(p.defectsRectified == null && p.defectsComplete == null)
          ? `${done} of ${p.defectsTotal} done · ${pct}%`
          : `${p.defectsRectified || 0} rectified · ${p.defectsComplete || 0} complete · ${pct}% done`}
      </div>
    </div>
  );
}

function EmptyState({ onCreate, hasFilter, user }) {
  // v0.55.0 — diagnostic empty-state. When there are genuinely no
  // locations for THIS signed-in workspace, name the workspace so the
  // screen reads as "this account has no sites yet" — not "the app is
  // broken". (Root cause of a mystery-empty list is almost always a
  // tenant-scope mismatch: locations were created under a different
  // TenantId than the one InSite-ID signs you into. See
  // sql/2026-07-06-diagnose-and-retag-locations.sql to realign.)
  const workspace = (user && (user.company || user.CompanyName)) || null;
  return (
    <div style={{ ...card, textAlign: 'center', padding: 60 }}>
      <div style={{ opacity: 0.3, marginBottom: 0 }}><Ruler size={48} /></div>
      <div style={{ fontSize: 16, fontWeight: 600, marginTop: 8 }}>
        {hasFilter ? 'No locations match' : 'No locations yet'}
      </div>
      <div style={{ fontSize: 13, color: theme.textLight, marginTop: 4, maxWidth: 380, marginLeft: 'auto', marginRight: 'auto' }}>
        {hasFilter
          ? 'Try a different search, or clear the filter.'
          : 'A location is a site / store / building. Each location can hold many reports. Add your first one to get started.'}
      </div>
      {!hasFilter && workspace && (
        <div style={{ fontSize: 12, color: theme.textLight, marginTop: 10 }}>
          Signed in to <strong style={{ color: theme.text }}>{workspace}</strong>. Locations you add appear here for everyone in this workspace.
        </div>
      )}
      {!hasFilter && (
        <button onClick={onCreate} style={{ ...btnPrimary, marginTop: 16 }}>+ New location</button>
      )}
    </div>
  );
}

function CreateProjectModal({ onClose, onSaved }) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function save() {
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError(null);
    try {
      const p = await apiSend('POST', '/api/projects', { name: name.trim(), address: address.trim() || null });
      onSaved(p);
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 16 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: theme.surface, width: 480, maxWidth: '100%', borderRadius: 10, padding: 22 }}
      >
        <h3 style={{ margin: '0 0 14px', fontSize: 18 }}>New location</h3>
        {error && (
          <div style={{ padding: 8, background: theme.bg, border: `1px solid ${theme.danger}`, color: theme.danger, borderRadius: 6, fontSize: 12, marginBottom: 10 }}>
            {error}
          </div>
        )}
        <div style={{ marginBottom: 12 }}>
          <Label>Name *</Label>
          <input autoFocus value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Bayview Heights pre-handover" style={input} />
        </div>
        <div style={{ marginBottom: 18 }}>
          <Label>Address</Label>
          <input value={address} onChange={e => setAddress(e.target.value)} placeholder="123 Smith St, Subiaco" style={input} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={btnSecondary}>Cancel</button>
          <button onClick={save} disabled={saving || !name.trim()} style={{ ...btnPrimary, opacity: (saving || !name.trim()) ? 0.5 : 1 }}>
            {saving ? 'Saving…' : 'Create location'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Label({ children }) {
  return <div style={{ fontSize: 11, fontWeight: 600, color: theme.textLight, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 5 }}>{children}</div>;
}
